import { useReducer } from 'react';

/**
 * Returns a function that, when called, forces a state change of the
 * enclosing functional component.
 */
export function useForceUpdate() {
    const [, forceUpdate] = useReducer((x) => !x, false);
    return forceUpdate;
}

export type Recycler<T> = (prev: T, next: T) => T;

export function makeNullableRecycler<T>(
    recycler: Recycler<T>
): Recycler<T | null> {
    return (prev: T | null, next: T | null) => {
        // This *may* look like it's missing the both-are-null case, but
        // it's not necessary, because null has value identity. So if both
        // are null, the following line will return a null, React's *own*
        // `Object.is` comparison will pass, and no change will be
        // registered.
        if (prev === null || next === null) return next;
        return recycler(prev, next);
    };
}

export function recycleEqualArray<T>(
    prev: ReadonlyArray<T>,
    next: ReadonlyArray<T>
): ReadonlyArray<T> {
    if (prev === next) return prev;
    if (prev.length !== next.length) return next;
    for (let i = 0; i < prev.length; i++) {
        if (!Object.is(prev[i], next[i])) return next;
    }
    return prev;
}

export const recycleEqualNullableArray =
    makeNullableRecycler(recycleEqualArray);
